// Chefi account: passkeys on the web, Telegram sign-in inside the Mini App, the link between the
// two, and recovery codes as the safety net. Everything else in the app works without an account.
import React, { useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Body, Button, Caption, Field, ListItem, Screen, Section, Title } from '../components/ui';
import { confirm } from '../components/Dialogs';
import ErrorNote, { useWait } from '../components/ErrorNote';
import Icon from '../components/Icon';
import SyncBox from '../components/SyncBox';
import CreatorSection from '../components/CreatorSection';
import FriendArchiveSettings from '../components/FriendArchiveSettings';
import CloudSyncBox from '../components/CloudSyncBox';
import RecoveryKitPanel from '../components/RecoveryKitPanel';
import { useFriends } from '../store/friends';
import { useSocial } from '../store/social';
import { useAccount } from '../store/account';
import { useCloud } from '../store/cloud';
import { useSettings } from '../store/settings';
import * as auth from '../platform/auth';
import { pickBinary } from '../platform/files';
import { NotARecoveryPdf, PdfNeedsPassword, readCodesFromPdf } from '../logic/recoveryKit';
import { telegram } from '../platform/telegram';
import { useCountdown } from '../hooks';
import { formatCountdown } from '../logic/format';
import { useNav } from '../navigation';
import { usePalette } from '../theme';

export default function AccountScreen() {
  const { t } = useTranslation();
  const p = usePalette();
  const nav = useNav();
  const { user, status, busy, phase, codes, recovered, run, dismissCodes } = useAccount();
  const wait = useWait();
  const [name, setName] = useState(useSettings.getState().displayName);
  const [lost, setLost] = useState(false);
  const [code, setCode] = useState('');
  const [pdf, setPdf] = useState<Uint8Array | null>(null);
  const [pdfPassword, setPdfPassword] = useState('');
  const [pdfProblem, setPdfProblem] = useState<string | null>(null);
  const codesLeft = useCountdown(user?.recoveryCreate.nextAllowedAt);

  // Read an uploaded recovery PDF on this device and sign in with the codes inside it (one attempt for all).
  const signInWithPdf = async (bytes: Uint8Array, password?: string) => {
    setPdfProblem(null);
    try {
      const found = await readCodesFromPdf(bytes, password);
      setPdf(null);
      setPdfPassword('');
      run(async () => ({ user: await auth.recoverySignIn(found), recovered: true }));
    } catch (e) {
      if (e instanceof PdfNeedsPassword) {
        setPdf(bytes);
        setPdfProblem(password ? t('account.lost.wrongPassword') : null);
      } else if (e instanceof NotARecoveryPdf) setPdfProblem(t('account.lost.notRecovery'));
      else setPdfProblem(t('account.errors.generic'));
    }
  };

  if (!auth.authAvailable) return <Screen><Body secondary>{t('account.webOnly')}</Body></Screen>;
  if (status === 'loading') return <Screen><ActivityIndicator color={p.tint} /></Screen>;
  const inTelegram = telegram.isInside;
  const passkeyWarning = inTelegram && auth.passkeysSupported ? <Caption color={p.tint}>{t('account.passkeyTelegramWarning')}</Caption> : null;

  if (!user) {
    return (
      <Screen>
        <ListItem title={t('settings.super')} icon="super" onPress={() => nav.navigate('Super')} />
        <Title>{t('account.title')}</Title>
        <Body secondary>{t(auth.nativeAccount ? 'account.nativeIntro' : inTelegram ? 'account.telegramIntro' : 'account.intro')}</Body>
        {auth.passkeysSupported ? (
          <>
            <Field label={t('settings.displayName')} value={name} onChangeText={setName} maxLength={40} />
            <Caption>{t('account.signupNote')}</Caption>
            <Button
              label={phase === 'check' ? t('account.checking') : t('account.create')} icon="lock" loading={busy && phase === 'check'} disabled={busy || wait > 0}
              onPress={() => run(async () => {
                const { user: u, recoveryCodes } = await auth.signUp(name, p2 => useAccount.setState({ phase: p2 }));
                return { user: u, codes: recoveryCodes };
              })}
            />
            <Button label={t('account.signIn')} icon="user" variant="secondary" disabled={busy} onPress={() => run(() => auth.signIn())} />
            {passkeyWarning}
          </>
        ) : (
          <><Caption>{t(inTelegram ? 'account.passkeyTelegramWarning' : auth.nativeAccount ? 'account.nativeCodeSignIn' : 'account.noPasskeys')}</Caption>{auth.nativeAccount ? <><Button label={t('account.createInBrowser')} variant="secondary" onPress={() => telegram.openLink('https://chefi.ceberon.ch/settings/account')} /><SyncBox /></> : null}</>
        )}
        {!inTelegram ? (
          lost ? (
            <Section title={t('account.lost.title')} icon="help">
              <Button label={t('account.lost.upload')} icon="import" variant="secondary" disabled={busy} onPress={async () => { const bytes = await pickBinary('.pdf,application/pdf'); if (bytes) signInWithPdf(bytes); }} />
              <Caption>{t('account.lost.uploadHint')}</Caption>
              {pdf ? (
                <>
                  <Field label={t('account.lost.password')} value={pdfPassword} onChangeText={setPdfPassword} autoCapitalize="none" autoCorrect={false} spellCheck={false} />
                  <Button label={t('account.lost.unlock')} disabled={busy || pdfPassword.length === 0} onPress={() => signInWithPdf(pdf, pdfPassword)} />
                </>
              ) : null}
              {pdfProblem ? <Caption color={p.danger}>{pdfProblem}</Caption> : null}
              <Field label={t('account.recovery.codeLabel')} value={code} onChangeText={setCode} placeholder="XXXX-XXXX-XXXX" autoCapitalize="characters" autoCorrect={false} maxLength={20} />
              <Button label={t('account.recovery.signInWithCode')} disabled={busy || code.trim().length < 12} loading={busy} onPress={() => run(async () => ({ user: await auth.recoverySignIn(code), recovered: true }))} />
              <Section title="Telegram"><SyncBox /></Section>
            </Section>
          ) : (
            <Button label={t('account.lost.title')} variant="plain" onPress={() => setLost(true)} />
          )
        ) : null}
        <ErrorNote />
      </Screen>
    );
  }

  const remaining = user.recoveryCodesRemaining;
  const { leftToday } = user.recoveryCreate;
  const lastLoginMethod = user.passkeys.length + (user.telegramLinked ? 1 : 0) <= 1;
  return (
    <Screen>
      <FriendArchiveSettings />
      <CreatorSection />
      <ListItem title={t('settings.super')} icon="super" onPress={() => nav.navigate('Super')} />
      <Title>{user.displayName || t('account.title')}</Title>
      {codes ? <RecoveryKitPanel codes={codes} displayName={user.displayName} onDone={dismissCodes} /> : null}
      {recovered && !codes ? <Body>{t(auth.nativeAccount ? 'account.nativeRecovered' : 'account.recovery.recoveredBanner')}</Body> : null}
      <Section title={t('account.passkeys')} icon="lock">
        {user.passkeys.map(k => (
          <ListItem
            key={k.id}
            title={k.label || t('account.passkey')}
            subtitle={t('account.addedOn', { date: new Date(k.createdAt).toLocaleDateString() })}
            icon="lock"
            right={lastLoginMethod ? undefined : <Icon name="trash" color={p.danger} />}
            onPress={lastLoginMethod ? undefined : async () => {
              if (await confirm(t('account.removeTitle'), t('account.removeBody'), t('common.delete'))) run(() => auth.removePasskey(k.id), true);
            }}
          />
        ))}
        {auth.passkeysSupported ? <Button label={t('account.addPasskey')} icon="plus" variant={recovered ? 'primary' : 'secondary'} disabled={busy} onPress={() => run(() => auth.addPasskey(), true)} /> : null}
        {passkeyWarning}
        <Caption>{t('account.passkeysNote')}</Caption>
      </Section>
      <Section title={t('account.recovery.title')} icon="lock">
        <Body style={remaining === 0 ? { color: p.danger } : undefined}>{remaining === 0 ? t('account.recovery.none') : t('account.recovery.remaining', { count: remaining })}</Body>
        {remaining > 0 && remaining <= 3 ? <Caption color={p.danger}>{t('account.recovery.low', { count: remaining })}</Caption> : null}
        <Button
          label={codesLeft > 0 ? t('account.codeTimer.wait', { time: formatCountdown(codesLeft) }) : remaining === 0 ? t('account.recovery.create') : t('account.recovery.renew')}
          icon={codesLeft > 0 ? 'clock' : undefined}
          variant={remaining === 0 && codesLeft === 0 ? 'primary' : 'secondary'}
          disabled={busy || codesLeft > 0}
          onPress={async () => {
            if (remaining > 0 && !(await confirm(t('account.recovery.renewTitle'), t('account.recovery.renewBody'), t('account.recovery.renew'), false))) return;
            run(async () => {
              const r = await auth.generateRecoveryCodes();
              return { user: r.user, codes: r.codes };
            }, true);
          }}
        />
        <Caption color={leftToday === 0 ? p.danger : undefined}>
          {leftToday === 0 && codesLeft > 0 ? t('account.codeTimer.dailyLimit', { time: formatCountdown(codesLeft) }) : t('account.codeTimer.left', { count: leftToday })}
        </Caption>
        <Caption>{t('account.recovery.note')} {t('account.codeTimer.limits')}</Caption>
      </Section>
      <Section title={t('account.cloud.title')} icon="reset">
        <CloudSyncBox />
      </Section>
      <Section title={t('account.devices.title')} icon="user">
        <Body>{t('account.devices.signedIn', { count: user.sessions })}</Body>
        {user.sessions > 1 ? <Button label={t('account.devices.revokeOthers')} variant="secondary" disabled={busy} onPress={() => run(() => auth.revokeOtherSessions(), true)} /> : null}
        <Caption>{t('account.devices.note')}</Caption>
      </Section>
      <Section title="Telegram">
        {user.telegramLinked ? (
          <>
            <Body>{t('account.telegramLinked')}</Body>
            {!inTelegram && user.passkeys.length ? <Button label={t('account.unlinkTelegram')} variant="secondary" disabled={busy} onPress={() => run(async () => { await auth.unlinkTelegram(); return { ...user, telegramLinked: false }; }, true)} /> : null}
          </>
        ) : inTelegram ? (
          <Caption>{t('sync.insideTelegram')}</Caption>
        ) : (
          <SyncBox />
        )}
      </Section>
      {auth.nativeAccount ? <Section title={t('account.confirmNative')} icon="lock">
        <Caption>{t('account.confirmNativeNote')}</Caption>
        <Field label={t('account.recovery.codeLabel')} value={code} onChangeText={setCode} placeholder="XXXX-XXXX-XXXX" autoCapitalize="characters" autoCorrect={false} maxLength={20} />
        <Button label={t('account.confirmNative')} disabled={busy || code.trim().length < 12} onPress={() => run(async () => { const confirmedUser = await auth.recoverySignIn(code); setCode(''); return confirmedUser; })} />
      </Section> : null}
      <ErrorNote />
      <Button label={t('account.signOut')} variant="secondary" disabled={busy} onPress={() => run(async () => { await auth.signOut(); useSocial.getState().forget(); useFriends.getState().forget(); return { user: null, codes: null, recovered: false }; })} />
      <Button
        label={t('account.delete.button')}
        variant="plain"
        disabled={busy}
        onPress={async () => {
          if (!(await confirm(t('account.delete.title'), t('account.delete.body'), t('account.delete.confirm')))) return;
          run(async () => {
            await auth.deleteAccount();
            useSocial.getState().forget(); useFriends.getState().forget();
            useCloud.setState({ accountId: null, lastSyncAt: null });
            return { user: null, codes: null, recovered: false };
          }, true);
        }}
      />
    </Screen>
  );
}
